import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SupplierPayments.css';

const SupplierPayments = ({ itineraryId, totalAmount, onPaymentsUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_to: '',
    component_name: '',
    payment_method: '',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    if (itineraryId) {
      fetchPayments();
      fetchClientPayments();
    }
  }, [itineraryId]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/itineraries/${itineraryId}/supplier-payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPayments(response.data);
      if (onPaymentsUpdate) {
        onPaymentsUpdate(response.data);
      }
    } catch (err) {
      console.error('Error fetching supplier payments:', err);
      setError('Failed to load supplier payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Filter for completed payments for THIS specific itinerary only
      const completedPayments = response.data.filter(p => 
        p.status === 'completed' && 
        p.itinerary_id === parseInt(itineraryId)
      );
      setClientPayments(completedPayments);
    } catch (err) {
      console.error('Error fetching client payments:', err);
      // Don't show error to user, just log it
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_to: '',
      component_name: '',
      payment_method: '',
      reference_number: '',
      notes: ''
    });
    setEditingPayment(null);
  };

  const handleAddPayment = () => {
    resetForm();
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const handleEditPayment = (payment) => {
    setFormData({
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_to: payment.payment_to || '',
      component_name: payment.component_name || '',
      payment_method: payment.payment_method || '',
      reference_number: payment.reference_number || '',
      notes: payment.notes || ''
    });
    setEditingPayment(payment);
    setShowAddModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setSuccess('');

    if (!formData.payment_date || !formData.amount) {
      setError('Payment date and amount are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      console.log('Submitting payment:', {
        itineraryId,
        formData,
        hasToken: !!token
      });
      
      if (editingPayment) {
        const response = await axios.put(
          `/api/itineraries/${itineraryId}/supplier-payments/${editingPayment.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Payment updated:', response.data);
        setSuccess('Payment updated successfully');
      } else {
        console.log('Making POST request to:', `/api/itineraries/${itineraryId}/supplier-payments`);
        const response = await axios.post(
          `/api/itineraries/${itineraryId}/supplier-payments`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Payment added successfully:', response.data);
        setSuccess('Payment added successfully');
      }

      console.log('Refreshing payment lists...');
      await fetchPayments();
      await fetchClientPayments();
      console.log('Closing modal...');
      setShowAddModal(false);
      resetForm();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving payment:', err);
      console.error('Error response:', err.response);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save payment';
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
      // Don't close modal on error
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/itineraries/${itineraryId}/supplier-payments/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Payment deleted successfully');
      await fetchPayments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting payment:', err);
      setError('Failed to delete payment');
    }
  };

  // Calculate total paid: supplier payments + client deposit payments
  const supplierPaymentsTotal = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const clientPaymentsTotal = clientPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalPaid = supplierPaymentsTotal + clientPaymentsTotal;
  const remainingBalance = parseFloat(totalAmount || 0) - totalPaid;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="supplier-payments-section">
      <div className="section-header">
        <div>
          <h3>Supplier Payments</h3>
          <p className="section-description">
            Track payments made to suppliers (hotels, airlines, cruise lines, etc.)
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleAddPayment}>
          + Add Payment
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Payment Summary */}
      <div className="payment-summary">
        <div className="summary-card">
          <div className="summary-label">Total Itinerary Cost</div>
          <div className="summary-value">{formatCurrency(totalAmount || 0)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Paid to Suppliers</div>
          <div className="summary-value paid">{formatCurrency(totalPaid)}</div>
          {clientPaymentsTotal > 0 && (
            <div className="summary-breakdown">
              <div className="breakdown-item">
                Client Deposits: {formatCurrency(clientPaymentsTotal)}
              </div>
              <div className="breakdown-item">
                Direct Payments: {formatCurrency(supplierPaymentsTotal)}
              </div>
            </div>
          )}
        </div>
        <div className="summary-card">
          <div className="summary-label">Remaining Balance</div>
          <div className={`summary-value ${remainingBalance > 0 ? 'remaining' : 'complete'}`}>
            {formatCurrency(remainingBalance)}
          </div>
        </div>
      </div>

      {/* Payments List */}
      {loading ? (
        <div className="loading-state">Loading payments...</div>
      ) : payments.length === 0 ? (
        <div className="empty-state">
          <p>No supplier payments recorded yet.</p>
          <p className="empty-state-hint">
            Click "Add Payment" to record payments made to suppliers for this itinerary.
          </p>
        </div>
      ) : (
        <div className="payments-table-container">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Paid To</th>
                <th>Component</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.payment_date)}</td>
                  <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                  <td>{payment.payment_to || '-'}</td>
                  <td>{payment.component_name || '-'}</td>
                  <td>{payment.payment_method || '-'}</td>
                  <td>{payment.reference_number || '-'}</td>
                  <td className="notes-cell">
                    {payment.notes ? (
                      <span className="notes-preview" title={payment.notes}>
                        {payment.notes.length > 50 
                          ? `${payment.notes.substring(0, 50)}...` 
                          : payment.notes}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="btn-icon btn-edit" 
                      onClick={() => handleEditPayment(payment)}
                      title="Edit payment"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-icon btn-delete" 
                      onClick={() => handleDeletePayment(payment.id)}
                      title="Delete payment"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Payment Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPayment ? 'Edit Payment' : 'Add Supplier Payment'}</h3>
              <button className="close-button" onClick={() => setShowAddModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="payment_date">Payment Date *</label>
                    <input
                      type="date"
                      id="payment_date"
                      name="payment_date"
                      value={formData.payment_date}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="amount">Amount *</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="payment_to">Paid To (Supplier)</label>
                    <input
                      type="text"
                      id="payment_to"
                      name="payment_to"
                      value={formData.payment_to}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., Hilton Hotels, Delta Airlines"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="component_name">Component</label>
                    <input
                      type="text"
                      id="component_name"
                      name="component_name"
                      value={formData.component_name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., Hotel, Flight, Cruise"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="payment_method">Payment Method</label>
                    <select
                      id="payment_method"
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Select method...</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Check">Check</option>
                      <option value="Wire Transfer">Wire Transfer</option>
                      <option value="ACH">ACH</option>
                      <option value="Cash">Cash</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="reference_number">Reference Number</label>
                    <input
                      type="text"
                      id="reference_number"
                      name="reference_number"
                      value={formData.reference_number}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Check #, Confirmation #, etc."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="form-input"
                    placeholder="Additional details about this payment..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPayment ? 'Update Payment' : 'Add Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPayments;
